import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { handleContactSubmit, contactInfo } from '../mock';

export const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    equipmentType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = handleContactSubmit(formData);
      if (result.success) {
        toast({
          title: 'Success!',
          description: result.message,
          duration: 5000,
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          equipmentType: '',
          message: ''
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Hello! I am interested in your medical equipment.');
    const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
      <div className="container mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wide">Get in Touch</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">
            Ready to Upgrade Your Medical Facility?
          </h2>
          <p className="text-lg text-slate-600">
            Contact us today for a consultation or to request a quote for our premium medical equipment.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="border-slate-200 hover:border-teal-300 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-2">
                  <Phone className="text-teal-600" size={24} />
                </div>
                <CardTitle className="text-lg">Phone</CardTitle>
                <CardDescription>
                  <a href={`tel:${contactInfo.phone}`} className="text-teal-600 hover:text-teal-700 transition-colors">
                    {contactInfo.phone}
                  </a>
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-slate-200 hover:border-teal-300 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-2">
                  <Mail className="text-teal-600" size={24} />
                </div>
                <CardTitle className="text-lg">Email</CardTitle>
                <CardDescription>
                  <a href={`mailto:${contactInfo.email}`} className="text-teal-600 hover:text-teal-700 transition-colors break-all">
                    {contactInfo.email}
                  </a>
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-slate-200 hover:border-teal-300 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-2">
                  <MessageCircle className="text-teal-600" size={24} />
                </div>
                <CardTitle className="text-lg">WhatsApp</CardTitle>
                <CardDescription>
                  <button
                    onClick={handleWhatsAppClick}
                    className="text-teal-600 hover:text-teal-700 transition-colors"
                  >
                    {contactInfo.whatsapp}
                  </button>
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-slate-200 hover:border-teal-300 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-2">
                  <MapPin className="text-teal-600" size={24} />
                </div>
                <CardTitle className="text-lg">Office Address</CardTitle>
                <CardDescription className="text-slate-600 leading-relaxed">
                  {contactInfo.address.line1}<br />
                  {contactInfo.address.line2}<br />
                  {contactInfo.address.city}, {contactInfo.address.state}<br />
                  {contactInfo.address.pincode}, {contactInfo.address.country}
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="bg-teal-50 rounded-xl p-6 border border-teal-100">
              <h3 className="font-bold text-slate-900 mb-3">Business Hours</h3>
              <p className="text-slate-600 text-sm mb-2">{contactInfo.businessHours.weekdays}</p>
              <p className="text-slate-600 text-sm mb-2">{contactInfo.businessHours.saturday}</p>
              <p className="text-slate-600 text-sm font-semibold">{contactInfo.businessHours.emergency}</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-2xl">Send us a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and our team will get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your name"
                        className="border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@example.com"
                        className="border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 1234567890"
                        className="border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="organization" className="block text-sm font-medium text-slate-700 mb-2">
                        Organization
                      </label>
                      <Input
                        id="organization"
                        name="organization"
                        type="text"
                        value={formData.organization}
                        onChange={handleChange}
                        placeholder="Hospital/Clinic Name"
                        className="border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="equipmentType" className="block text-sm font-medium text-slate-700 mb-2">
                      Equipment Type
                    </label>
                    <select
                      id="equipmentType"
                      name="equipmentType"
                      value={formData.equipmentType}
                      onChange={handleChange}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 focus:border-teal-500 focus:ring-2 focus:ring-teal-500 outline-none transition-colors"
                    >
                      <option value="">Select equipment type</option>
                      <option value="CT Scanner">CT Scanner</option>
                      <option value="MRI System">MRI System</option>
                      <option value="Cath Lab">Cath Lab</option>
                      <option value="PET CT">PET CT</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your requirements..."
                      rows={5}
                      className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    size="lg"
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white transition-colors group"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};